import { useMemo } from 'react';

import { ACCOUNTING_STATUS_CONFIG } from '../constants/accountingEntriesConstants';
import {
  getAccountingOriginLabel,
  getEntryMonthKey,
  roundMoney,
  sumAccountingLines,
} from '../utils/accountingEntriesUtils';

const useAccountingDashboardData = (entries, ledgerRows) => {
  const totals = useMemo(() => entries.reduce((acc, entry) => {
    const entryTotals = sumAccountingLines(entry.lines || []);
    return {
      debit: roundMoney(acc.debit + entryTotals.debit),
      credit: roundMoney(acc.credit + entryTotals.credit),
      draft: acc.draft + (entry.status === 'draft' ? 1 : 0),
      posted: acc.posted + (entry.status === 'posted' ? 1 : 0),
      cancelled: acc.cancelled + (entry.status === 'cancelled' ? 1 : 0),
    };
  }, {
    debit: 0,
    credit: 0,
    draft: 0,
    posted: 0,
    cancelled: 0,
  }), [entries]);

  const monthlyData = useMemo(() => {
    const grouped = new Map();
    entries.forEach((entry) => {
      const month = getEntryMonthKey(entry);
      if (!month) return;
      const entryTotals = sumAccountingLines(entry.lines || []);
      const item = grouped.get(month.key) || {
        key: month.key,
        label: month.label,
        asientos: 0,
        debe: 0,
        haber: 0,
      };
      item.asientos += 1;
      item.debe = roundMoney(item.debe + entryTotals.debit);
      item.haber = roundMoney(item.haber + entryTotals.credit);
      grouped.set(month.key, item);
    });
    return [...grouped.values()]
      .sort((left, right) => left.key.localeCompare(right.key))
      .slice(-12);
  }, [entries]);

  const originData = useMemo(() => {
    const grouped = new Map();
    entries.forEach((entry) => {
      const entryTotals = sumAccountingLines(entry.lines || []);
      const name = getAccountingOriginLabel(entry.source_type);
      const item = grouped.get(name) || { name, debe: 0, haber: 0, asientos: 0 };
      item.asientos += 1;
      item.debe = roundMoney(item.debe + entryTotals.debit);
      item.haber = roundMoney(item.haber + entryTotals.credit);
      grouped.set(name, item);
    });
    return [...grouped.values()].sort((left, right) => right.asientos - left.asientos);
  }, [entries]);

  const statusData = useMemo(() => Object.entries(ACCOUNTING_STATUS_CONFIG).map(([status, config]) => ({
    name: config.label,
    value: entries.filter((entry) => entry.status === status).length,
  })).filter((row) => row.value > 0), [entries]);

  const topMovementAccounts = useMemo(() => ledgerRows
    .map((row) => ({
      name: row.account_label || 'Sin cuenta',
      value: roundMoney(Math.abs(row.debit) + Math.abs(row.credit)),
    }))
    .filter((row) => row.value > 0)
    .sort((left, right) => right.value - left.value)
    .slice(0, 8), [ledgerRows]);

  const balanceAccounts = useMemo(() => ledgerRows
    .map((row) => ({
      name: row.account_label || 'Sin cuenta',
      balance: Math.abs(row.balance),
    }))
    .filter((row) => row.balance > 0)
    .sort((left, right) => right.balance - left.balance)
    .slice(0, 8), [ledgerRows]);

  const accountsWithBalanceCount = useMemo(() => ledgerRows
    .filter((row) => Math.abs(Number(row.balance || 0)) > 0)
    .length, [ledgerRows]);

  const difference = roundMoney(totals.debit - totals.credit);
  const balancedEntries = entries.filter((entry) => {
    const entryTotals = sumAccountingLines(entry.lines || []);
    return roundMoney(entryTotals.debit - entryTotals.credit) === 0;
  }).length;
  const balancedRatio = entries.length ? (balancedEntries / entries.length) * 100 : 0;

  return {
    accountsWithBalanceCount,
    balanceAccounts,
    balancedRatio,
    difference,
    monthlyData,
    originData,
    statusData,
    topMovementAccounts,
    totals,
  };
};

export default useAccountingDashboardData;
